import { useCallback, useState } from 'react';

import { Icon, IconButton } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { FlatList, FlatListProps, Image, Platform, StyleSheet, Text, View } from 'react-native';

import { ActionButtonSizes } from 'components/action-button';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import { routes } from 'navigation/routing';
import { ILoyaltyBadge } from 'pages/loyalty/loyalty-badges/types';
import { primitive } from 'styles/constants/primitives';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';

import { AboutBadgesModal } from './about-badges-modal';
import { BADGE_CARD_WIDTH, BadgeCard } from './badge-card';
import { BadgesSectionSkeleton } from './badges-section-skeleton';

const awardRibbon = require('./award-ribbon.png');

interface IBadgesSectionProps {
  loading?: boolean;
  badges?: ILoyaltyBadge[];
}

export const BadgesSection = ({ loading, badges }: IBadgesSectionProps) => {
  const { navigate } = useRouter();
  const { formatMessage } = useIntl();
  const showCarousel = !isMobile();
  const [showAboutModal, setShowAboutModal] = useState(false);

  const renderItem: FlatListProps<ILoyaltyBadge>['renderItem'] = useCallback(
    ({ item, index }) => (
      <BadgeCard
        badge={item}
        onPress={() => navigate(routes.badgeDetail(item.id))}
        accessibilityRole="link"
        style={[!showCarousel && index === badges?.length - 1 && styles.lastBadge]}
      />
    ),
    [navigate, showCarousel, badges?.length]
  );

  return (
    <View style={styles.wrapper}>
      <View style={styles.title}>
        <Image style={styles.titleIcon} source={awardRibbon} />
        <Text style={styles.titleText}>
          {formatMessage({ id: 'badges', defaultMessage: 'Badges' })}
        </Text>
        <IconButton
          size={ActionButtonSizes.SMALL}
          onPress={() => setShowAboutModal(true)}
          icon={<Icon variant="info" size="$5" />}
        />
      </View>
      {loading && <BadgesSectionSkeleton />}
      {!loading &&
        !!badges &&
        (showCarousel ? (
          <ResponsiveCarousel
            loop={false}
            itemWidth={BADGE_CARD_WIDTH}
            data={badges}
            keyExtractor={item => item.id}
            renderItem={renderItem}
          />
        ) : (
          <FlatList
            style={styles.badgeList}
            contentContainerStyle={styles.badgeListContentContainer}
            horizontal
            data={badges}
            keyExtractor={item => item.id}
            showsHorizontalScrollIndicator={false}
            renderItem={renderItem}
          />
        ))}
      {showAboutModal && <AboutBadgesModal onClose={() => setShowAboutModal(false)} />}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    gap: isMobile() ? primitive.spacing.$3 : primitive.spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    width: '100%',
  },
  title: {
    paddingHorizontal: 18,
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  titleText: {
    fontSize: primitive.fontSizes.lg,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  titleIcon: {
    width: 24,
    height: 24,
  },
  badgeList: {
    paddingLeft: primitive.spacing.$4,
  },
  badgeListContentContainer: {
    gap: primitive.spacing.$2,
    paddingRight: primitive.spacing.$4,
  },
  lastBadge: Platform.select({
    android: {
      marginRight: primitive.spacing.$4,
    },
  }),
});
